import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useCallback } from 'react';
const SearchInput = ({ id, dashboardState, mapData, searchText, onSearchTextChange, onSelect }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchIndex, setSearchIndex] = useState([]);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    // Normalize text for search matching
    const normalizeText = (text) => {
        return (text || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .replace(/[^a-z0-9\s]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    };
    // Build search index when map data changes
    useEffect(() => {
        if (!mapData || !Array.isArray(mapData)) {
            setSearchIndex([]);
            return;
        }
        const newIndex = [];
        mapData.forEach((point, index) => {
            if (!point)
                return;
            const name = point.name || '';
            const state = point.state || '';
            const hcKey = point['hc-key'] || '';
            let stateCode = hcKey.replace('us-', '').toUpperCase();
            const dasIdx = stateCode.indexOf('-');
            const result = dasIdx !== -1 ? stateCode.substring(0, dasIdx) : stateCode;
            stateCode = result;
            let label = name;
            let searchTerms = [name];
            if (dashboardState.geoLevel === 'county' && state) {
                label = `${name}, ${state}`;
                searchTerms.push(state);
            }
            else if (dashboardState.geoLevel === 'state' && stateCode) {
                label = `${name} (${stateCode})`;
                searchTerms.push(stateCode);
            }
            const search = normalizeText(searchTerms.filter(Boolean).join(' '));
            newIndex.push({
                index,
                label,
                search,
                stateName: dashboardState.geoLevel === 'state' ? name : state,
                countyName: dashboardState.geoLevel === 'county' ? name : undefined,
                stateCode
            });
        });
        setSearchIndex(newIndex);
    }, [mapData, dashboardState.geoLevel]);
    // Search suggestion algorithm
    const getSuggestions = useCallback((query) => {
        const normalizedQuery = normalizeText(query);
        if (!normalizedQuery)
            return [];
        const parts = normalizedQuery.split(' ').filter(Boolean);
        const scored = searchIndex
            .map(item => {
            let score = 0;
            // Exact prefix match gets highest score
            if (item.search.startsWith(normalizedQuery)) {
                score += 100;
            }
            // Each word part match adds score
            parts.forEach(part => {
                if (item.search.includes(part)) {
                    score += 10;
                }
            });
            return { ...item, score };
        })
            .filter(x => x.score > 0)
            .sort((a, b) => {
            // Sort by score, then alphabetically
            if (b.score !== a.score)
                return b.score - a.score;
            return a.label.localeCompare(b.label);
        })
            .slice(0, 12); // Limit to 12 results
        return scored;
    }, [searchIndex]);
    // Handle input changes
    const handleInputChange = (e) => {
        const value = e.target.value;
        onSearchTextChange(value);
        if (value.trim()) {
            const results = getSuggestions(value);
            setSuggestions(results);
            setShowDropdown(results.length > 0);
            setActiveIndex(-1);
        }
        else {
            setSuggestions([]);
            setShowDropdown(false);
        }
    };
    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!showDropdown || suggestions.length === 0)
            return;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(prev => (prev + 1) % suggestions.length);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
                break;
            case 'Enter':
                e.preventDefault();
                if (activeIndex >= 0 && suggestions[activeIndex]) {
                    handleSelect(suggestions[activeIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setShowDropdown(false);
                setActiveIndex(-1);
                break;
        }
    };
    // Handle selection
    const handleSelect = (item) => {
        var _a;
        onSearchTextChange(item.label);
        setShowDropdown(false);
        setActiveIndex(-1);
        // Call the onSelect callback
        if (dashboardState.geoLevel === 'county' && item.countyName && item.stateName) {
            // For county selection, we need to pass the county name and state
            onSelect(item.stateName, item.countyName, item.stateCode);
        }
        else if (dashboardState.geoLevel === 'state' && item.stateName) {
            // For state selection, just pass the state name
            onSelect(item.stateName, undefined, item.stateCode);
        }
        // Blur the input to hide keyboard on mobile
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
    };
    // Handle blur
    const handleBlur = () => {
        // Delay to allow click events to fire first
        setTimeout(() => {
            setShowDropdown(false);
        }, 150);
    };
    // Clear search
    const clearSearch = () => {
        onSearchTextChange('');
        setSuggestions([]);
        setShowDropdown(false);
        setActiveIndex(-1);
    };
    // Update placeholder based on geo level
    const placeholder = dashboardState.geoLevel === 'state'
        ? 'Search states...'
        : 'Search counties...';
    // Derive the dropdown/option ids from the input id so multiple instances stay unique (508)
    const dropdownId = `${id}-dropdown`;
    const optionId = (index) => `${id}-item-${index}`;
    // Clear search when geo level changes
    useEffect(() => {
        clearSearch();
    }, [dashboardState.geoLevel]);
    return (_jsxs("div", { className: "mapdashboard-toolbar-search", children: [_jsxs("label", { htmlFor: id, className: "form-label visually-hidden", children: ["Search ", dashboardState.geoLevel === 'state' ? 'states' : 'counties'] }), _jsx("i", { className: "bi bi-search", "aria-hidden": "true" }), _jsx("input", { ref: inputRef, type: "text", id: id, placeholder: placeholder, value: searchText, onChange: handleInputChange, onKeyDown: handleKeyDown, onBlur: handleBlur, "aria-label": `Search ${dashboardState.geoLevel === 'state' ? 'states' : 'counties'}`, "aria-expanded": showDropdown, "aria-controls": dropdownId, "aria-activedescendant": activeIndex >= 0 ? optionId(activeIndex) : undefined, autoComplete: "off" }), showDropdown && suggestions.length > 0 && (_jsx("div", { ref: dropdownRef, id: dropdownId, className: "typeahead-menu", style: { display: 'block' }, role: "listbox", "aria-label": "Search suggestions", children: suggestions.map((item, index) => (_jsx("div", { id: optionId(index), className: `typeahead-item ${index === activeIndex ? 'active' : ''}`, role: "option", "aria-selected": index === activeIndex, onMouseDown: (e) => {
                        e.preventDefault(); // Prevent blur
                        handleSelect(item);
                    }, onMouseEnter: () => setActiveIndex(index), children: item.label }, `${item.index}-${item.label}`))) }))] }));
};
export default SearchInput;
